import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { Ticket } from '../../types';
import QRCode from 'react-qr-code';
import {
  TicketIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const TicketDetail: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTicket = async () => {
      if (!ticketId) {
        setError('No ticket ID provided');
        setLoading(false);
        return;
      }

      try {
        const ticketDoc = await getDoc(doc(db, 'tickets', ticketId));
        if (!ticketDoc.exists()) {
          setError('Ticket not found');
          setLoading(false);
          return;
        }

        const data = ticketDoc.data();
        
        // Handle date formatting
        const handleDate = (dateValue: any): string => {
          if (!dateValue) return new Date().toISOString();
          
          // Firebase timestamp
          if (typeof dateValue === 'object' && 'seconds' in dateValue) {
            return new Date(dateValue.seconds * 1000).toISOString();
          }
          
          // Already a string
          if (typeof dateValue === 'string') {
            return dateValue;
          }
          
          // Default
          return new Date(dateValue).toISOString();
        };

        setTicket({
          id: ticketDoc.id,
          name: data.name || 'Unknown',
          email: data.email || 'Unknown',
          company: data.company || '',
          eventId: data.eventId,
          eventTitle: data.eventTitle || 'Unknown Event',
          eventDate: data.eventDate,
          eventTime: data.eventTime,
          eventLocation: data.eventLocation,
          purchaserUID: data.purchaserUID,
          purchaseDate: handleDate(data.purchaseDate),
          paymentId: data.paymentId || ticketDoc.id,
          status: data.status || 'active'
        });
      } catch (err) {
        console.error('Error loading ticket:', err);
        setError('Failed to load ticket details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId]);

  const formatDate = (dateStr: string | any) => {
    try {
      // Handle Firestore timestamp or ISO string
      if (typeof dateStr === 'object' && dateStr && 'toDate' in dateStr) {
        return format(dateStr.toDate(), 'MMM d, yyyy');
      }
      if (typeof dateStr === 'string') {
        return format(new Date(dateStr), 'MMM d, yyyy');
      }
      return dateStr;
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateStr;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error || 'An error occurred when loading the ticket'}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto bg-white shadow overflow-hidden my-8 print:shadow-none print:my-0">
      {/* Print only visible on screen */}
      <div className="print:hidden mb-4 text-right">
        <button 
          onClick={handlePrint}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Print Ticket
        </button>
      </div>
      
      <div className="border-2 border-gray-200 rounded-lg p-6 relative">
        {/* Status Badge */}
        <div className="absolute top-6 right-6">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            ticket.status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : ticket.status === 'used'
              ? 'bg-gray-100 text-gray-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
          </span>
        </div>

        {/* Header with logo */}
        <div className="flex items-center justify-between mb-6 border-b pb-4">
          <div>
            <h1 className="text-2xl font-bold text-blue-600">Event Ticket</h1>
            <p className="text-sm text-gray-500">Ticket ID: {ticket.id}</p>
          </div>
        </div>

        {/* Event Information */}
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-2">{ticket.eventTitle}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {ticket.eventDate && (
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 mr-3 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{ticket.eventDate}</p>
                </div>
              </div>
            )}
            
            {ticket.eventTime && (
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 mr-3 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="font-medium">{ticket.eventTime}</p>
                </div>
              </div>
            )}
          </div>
          
          {ticket.eventLocation && (
            <div className="flex items-center mt-4">
              <MapPinIcon className="h-5 w-5 mr-3 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">{ticket.eventLocation}</p>
              </div>
            </div>
          )}
        </div>

        {/* Attendee Information */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-2 flex items-center">
            <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
            Attendee Information
          </h3>
          <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <span className="text-sm text-gray-500">Name: </span>
              <span className="font-medium">{ticket.name}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Email: </span>
              <span>{ticket.email}</span>
            </div>
            {ticket.company && (
              <div className="md:col-span-2">
                <span className="text-sm text-gray-500">Company: </span>
                <span>{ticket.company}</span>
              </div>
            )}
          </div>
        </div>

        {/* QR Code
        <div className="flex flex-col items-center justify-center mb-6">
          <p className="text-sm text-gray-500 mb-2">Please scan at the event</p>
          <div className="p-3 bg-white border-2 border-gray-200 rounded-lg">
            <QRCode 
              value={`${window.location.origin}/tickets/verify/${ticket.id}`} 
              size={140}
            />
          </div>
        </div> */}

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-4 pt-4 border-t">
          <p>Purchased on {formatDate(ticket.purchaseDate)}</p>
          <p>Payment ID: {ticket.paymentId}</p>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail; 